import React, { useState, useEffect } from 'react';
import { FiX, FiTrendingUp, FiSave } from 'react-icons/fi';
import toast from 'react-hot-toast';
import metaVendaService from '../services/metaVenda';

const MetasVendaModal = ({ isOpen, onClose, projecaoAtual, onMetasSalvas }) => {
    // metas: armazena todos os meses na chave 'Mês' para envio em lote
    const [metas, setMetas] = useState({});
    const [mesSelecionado, setMesSelecionado] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && projecaoAtual && projecaoAtual.length > 0) {
            const stateInicial = {};
            projecaoAtual.forEach(item => {
                stateInicial[item.mes] = {
                    valorMetaVenda: item.valorMetaVenda || 0,
                    saldoInicial: item.saldoInicial || 0,
                    resultadoAlcancado: item.resultadoAlcancado || 0,
                    projecaoFinanceira: item.projecaoFinanceira || 0,
                    projecaoFinanceiraAlcancada: item.projecaoFinanceiraAlcancada || 0,
                    despesasFixas: item.despesasFixas || 0,
                    comprasNacional: item.comprasNacional || 0,
                    investimentos: item.investimentos || 0
                };
            });
            setMetas(stateInicial);
            if (!mesSelecionado || !stateInicial[mesSelecionado]) {
                setMesSelecionado(projecaoAtual[0].mes);
            }
        }
    }, [isOpen, projecaoAtual]);

    const handleInputChange = (campo, valor) => {
        setMetas(prev => ({
            ...prev,
            [mesSelecionado]: {
                ...prev[mesSelecionado],
                [campo]: valor
            }
        }));
    };

    const handleSalvar = async () => {
        setIsSubmitting(true);
        try {
            const payload = Object.keys(metas).map(mes => ({
                mesReferencia: mes,
                valorMeta: Number(metas[mes].valorMetaVenda) || 0,
                saldoInicial: Number(metas[mes].saldoInicial) || 0,
                resultadoAlcancado: Number(metas[mes].resultadoAlcancado) || 0,
                projecaoFinanceira: Number(metas[mes].projecaoFinanceira) || 0,
                projecaoFinanceiraAlcancada: Number(metas[mes].projecaoFinanceiraAlcancada) || 0,
                despesasFixas: Number(metas[mes].despesasFixas) || 0,
                comprasNacional: Number(metas[mes].comprasNacional) || 0,
                investimentos: Number(metas[mes].investimentos) || 0
            }));

            await metaVendaService.salvarMetasEmLote(payload);
            toast.success('Projeção mensal atualizada com sucesso!');
            onMetasSalvas(); // Atualiza o gráfico por trás
            onClose();
        } catch (error) {
            toast.error('Erro ao salvar os campos. Tente novamente.');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const campos = [
        { key: 'saldoInicial', label: 'Saldo Inicial' },
        { key: 'valorMetaVenda', label: 'Meta de Venda' },
        { key: 'resultadoAlcancado', label: 'Resultado Alcançado' },
        { key: 'projecaoFinanceira', label: 'Projeção Financeira' },
        { key: 'projecaoFinanceiraAlcancada', label: 'Projeção Financeira Alcançada' },
        { key: 'despesasFixas', label: 'Despesas Fixas' },
        { key: 'comprasNacional', label: 'Compras Nacionais' },
        { key: 'investimentos', label: 'Investimentos' }
    ];

    const valoresAtuais = metas[mesSelecionado] || {};

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
                <div className="flex justify-between items-center px-6 py-4 bg-emerald-50 border-b border-emerald-100">
                    <h3 className="text-lg font-bold text-emerald-800 flex items-center gap-2">
                        <FiTrendingUp className="text-emerald-600" /> Preencher Campos Manuais (Projeção)
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX size={24} /></button>
                </div>

                <div className="p-6 overflow-y-auto" style={{ maxHeight: '70vh'}}>
                    <div className="mb-6 flex items-center gap-4">
                        <label className="font-bold text-gray-700">Selecione o Mês para editar:</label>
                        <select 
                            value={mesSelecionado}
                            onChange={(e) => setMesSelecionado(e.target.value)}
                            className="bg-white border border-gray-300 text-gray-900 font-bold focus:ring-emerald-500 rounded-lg p-2 flex-1 outline-none"
                        >
                            {Object.keys(metas).map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {campos.map((campo) => (
                            <div key={campo.key} className="flex flex-col bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <label className="text-sm font-semibold text-gray-600 mb-2">{campo.label}</label>
                                <div className="relative w-full">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">R$</span>
                                    <input
                                        type="number"
                                        value={valoresAtuais[campo.key] || ''}
                                        onChange={(e) => handleInputChange(campo.key, e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 rounded-md border-gray-300 focus:ring-emerald-500 focus:border-emerald-500 text-right font-semibold text-gray-800"
                                        placeholder="0,00"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2 text-gray-500 font-bold hover:text-gray-700">Cancelar</button>
                    <button 
                        onClick={handleSalvar} 
                        disabled={isSubmitting}
                        className="px-6 py-2 bg-emerald-600 text-white rounded-xl font-black shadow-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
                    >
                        <FiSave /> {isSubmitting ? 'Salvando...' : 'Gravar Tudo'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MetasVendaModal;